import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './local-storage.service';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthToken } from '../models/auth.models';
import { Utils } from './utils';
import { AvailableApplication } from '../models/environment.interface';
import { DataService } from './data.service';
import {
    getLaunchRequestedCompanyId,
    setLaunchRequestedCompanyId,
} from '../utils/studies-launch-context.util';
import { normalizeAuthAppUrl } from '../utils/auth-app-url.util';

/** Response from POST /api/v1/auth/identity/session/refresh (Entra / Firebase). */
export interface SessionRefreshResponse {
    status?: string;
    token?: string;
    refreshToken?: string;
    email?: string;
    expiresIn?: number;
    expiresAt?: string;
    tokenType?: string;
}

/** Response from GET /api/v1/auth/identity/me. */
export interface IdentityMeResponse {
    user?: {
        id?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
    };
    activeOrganizationId?: string | null;
    email?: string;
    name?: string;
}

@Injectable({ providedIn: 'root' })

export class AuthenticationService {
    private readonly sessionRefreshIntervalMs = 5 * 60 * 1000;
    private readonly tokenRefreshBufferMs = 5 * 60 * 1000;
    private sessionRefreshTimer: ReturnType<typeof setInterval> | null = null;
    private refreshSessionPromise: Promise<string | null> | null = null;

    constructor(
        private readonly _localStorageService: LocalStorageService,
        private readonly _dataService: DataService) {
    }

    getNewAccessToken(refreshToken: string): Observable<AuthToken> {
        if (this.isLegacyRefreshToken(refreshToken)) {
            return this.getAccessTokenFromRefreshToken(refreshToken);
        }
        return this.refreshSessionViaIdentityApi(refreshToken).pipe(
            map((res) => ({
                accessToken: (res.token || '').trim(),
                refreshToken: (res.refreshToken || refreshToken || '').trim(),
            })),
            catchError(() => this.getAccessTokenFromRefreshToken(refreshToken))
        );
    }

    refreshSessionViaIdentityApi(
        refreshToken: string
    ): Observable<SessionRefreshResponse> {
        const url = `${this.adminApiBaseUrl}api/v1/auth/identity/session/refresh`;
        return this._dataService.post(url, { refreshToken });
    }

    getAccessTokenFromRefreshToken(refreshToken: string): Observable<AuthToken> {
        const url = `${this.adminApiBaseUrl}api/v1/auth/refresh-token`;
        const accessToken = this.authToken;
        const headers: Record<string, string> = {};
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`;
        }
        return this._dataService.postwithHeaders(
            url,
            { refreshToken },
            headers
        );
    }

    getLoggedInUser(): Observable<User> {
        const url = this.adminApiBaseUrl + 'api/v1/auth/currentUser';
        return this._dataService.get(url);
    }

    getIdentityMe(): Observable<IdentityMeResponse> {
        const url = `${this.adminApiBaseUrl}api/v1/auth/identity/me`;
        return this._dataService.get(url);
    }

    mapIdentityMeToUser(identity: IdentityMeResponse | null | undefined): User | null {
        const profile = identity?.user;
        const userId = String(profile?.id ?? '').trim();
        if (!userId) {
            return null;
        }
        const user = new User();
        user.id = userId;
        user.email = String(profile?.email ?? identity?.email ?? '').trim();
        user.firstName = String(profile?.firstName ?? '').trim();
        user.lastName = String(profile?.lastName ?? '').trim();
        const activeOrganizationId = String(identity?.activeOrganizationId ?? '').trim();
        if (activeOrganizationId) {
            setLaunchRequestedCompanyId(activeOrganizationId);
        }
        user.companyId = activeOrganizationId;
        return user;
    }

    resolveLoggedInUser(): Observable<User | null> {
        if (this.isIdentityAccessToken()) {
            return this.getIdentityMe().pipe(
                map((identity) => this.mapIdentityMeToUser(identity)),
                catchError(() => of(null))
            );
        }
        return this.getLoggedInUser().pipe(catchError(() => of(null)));
    }

    getUserCompanies(userId: string): Observable<UserCompany[]> {
        const url = `${this.adminApiBaseUrl}api/v1/users/${userId}/companies`;
        return this._dataService.get(url);
    }

    getCompanyProductsLicenses(): Observable<string[]> {
        return this._dataService.get(this.adminApiBaseUrl + 'api/v1/companies/company_licenses')
            .pipe(map((licenses) => licenses.map(l => l.name)))
    }

    getAuthTokenForCompany(_userId: string, companyId: string): Observable<AuthToken> {
        setLaunchRequestedCompanyId(companyId);
        const accessToken = (this.authToken || '').trim();
        const refreshToken = (this.refreshToken || '').trim();
        if (!accessToken || this.isLegacyAccessToken(accessToken)) {
            return throwError(
                () => new Error('Identity session required to select a company.')
            );
        }
        return of({ accessToken, refreshToken });
    }

    hideOrPinCompany(request: any): Observable<void> {
        return this._dataService.post(`${this.adminApiBaseUrl}api/v1/users/pin-hide-company`, request);
    }

    logoutAllApps(): Observable<void> {
        return this._dataService.post(`${this.adminApiBaseUrl}api/v1/auth/logout`, null);
    }

    deviceSessionCheck(): Observable<AuthToken> {
        if (!this._localStorageService.machineId) {
            return of(null);
        }
        return this._dataService.get(`${this.adminApiBaseUrl}api/v1/auth/check-session/${this._localStorageService.machineId}`);
    }

    get authToken(): string {
        if (!this._localStorageService.authorizationData) {
            return null;
        }
        return this._localStorageService.authorizationData.token;
    }

    get refreshToken(): string {
        if (!this._localStorageService.authorizationData) {
            return null;
        }
        return this._localStorageService.authorizationData.refreshToken;
    }

    get phiApiBaseUrl(): string {
        return environment.apiBaseUrl;
    }

    get adminApiBaseUrl(): string {
        return environment.adminApiBaseUrl;
    }

    logout(loggedOutFromAllApps: boolean = false): void {
        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;
        if (this._localStorageService.authorizationData) {
            this._localStorageService.authorizationData = null;
        }
        if (loggedOutFromAllApps) {
            const authBase = this.getStudiesAuthEntryBaseUrl().replace(/\/+$/, '');
            window.location.href = `${authBase}/logout`;
            return;
        }
        this.redirectToLogin(0);
    }

    buildStudiesLoginUrl(options?: { loggedOut?: boolean; handoffFailed?: boolean }): string {
        const base = this.getStudiesAuthEntryBaseUrl();
        const login = new URL(`${base.replace(/\/+$/, '')}/`);
        login.searchParams.set('app', AvailableApplication.Studies);
        if (options?.handoffFailed) {
            login.searchParams.set('handoffFailed', '1');
        } else if (options?.loggedOut) {
            login.searchParams.set('loggedOut', '1');
        }
        return login.href;
    }

    private getStudiesAuthEntryBaseUrl(): string {
        return normalizeAuthAppUrl(
            environment.authAppUrl?.trim() ||
            environment.loginUrl?.trim() ||
            ''
        );
    }

    clearSessionAndRedirectToLogin(options?: { handoffFailed?: boolean }): void {
        this.stopPeriodicSessionRefresh();
        this.refreshSessionPromise = null;
        this._localStorageService.authorizationData = null;
        this.redirectToLogin(0, options);
    }

    redirectToLogin(timeoutSecs: number = 1000, options?: { handoffFailed?: boolean }) {
        const loginUrl = this.buildStudiesLoginUrl(
            options?.handoffFailed ? { handoffFailed: true } : undefined
        );
        const navigate = () => window.location.replace(loginUrl);
        if (timeoutSecs <= 0) {
            navigate();
            return;
        }
        setTimeout(navigate, timeoutSecs);
    }

    get apiBaseUrl(): string {
        return environment.apiBaseUrl;
    }

    persistAuthData = (token: AuthToken): void => {
        let jwtPayload: { exp?: number } = {};
        try {
            jwtPayload = Utils.parseJwt(token.accessToken);
        } catch {
            jwtPayload = {};
        }
        const authData = {
            token: token.accessToken,
            refreshToken: token.refreshToken,
            expiresAt: jwtPayload.exp
                ? Utils.getTokenExpireDate(jwtPayload.exp)
                : new Date(Date.now() + 60 * 60 * 1000),
        };

        this._localStorageService.authorizationData = authData;
        if (authData.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
    }

    get isTokenExpired(): boolean {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token) {
            return true;
        }
        if (authData.expiresAt) {
            return new Date(authData.expiresAt) <= new Date();
        }
        return this.isJwtExpired(authData.token);
    }

    get isTokenExpiringSoon(): boolean {
        if (this.isTokenExpired) {
            return true;
        }
        const authData = this._localStorageService.authorizationData;
        if (authData?.expiresAt) {
            return (
                new Date(authData.expiresAt).getTime() - Date.now() <=
                this.tokenRefreshBufferMs
            );
        }
        return this.isJwtExpiringSoon(authData?.token);
    }

    async getValidAccessToken(): Promise<string | null> {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token?.trim()) {
            return null;
        }
        if (!this.isTokenExpiringSoon) {
            return authData.token;
        }
        if (!authData.refreshToken?.trim()) {
            return this.isTokenExpired ? null : authData.token;
        }
        const refreshed = await this.refreshSession('proactive');
        if (refreshed) {
            return refreshed;
        }
        return this.isTokenExpired ? null : authData.token;
    }

    isIdentityRefreshRequest(url: string): boolean {
        const normalized = (url || '').toLowerCase();
        return normalized.includes('/api/v1/auth/identity/session/refresh');
    }

    isLegacyRefreshRequest(url: string): boolean {
        const normalized = (url || '').toLowerCase();
        return normalized.includes('/api/v1/auth/refresh-token');
    }

    isLegacyRefreshToken(refreshToken: string | null | undefined): boolean {
        const payload = this.parseJwtPayload(String(refreshToken || '').trim());
        return payload?.['type'] === 'refresh_token';
    }

    isIdentityRefreshToken(refreshToken: string | null | undefined): boolean {
        const trimmed = String(refreshToken || '').trim();
        if (!trimmed || this.isLegacyRefreshToken(trimmed)) {
            return false;
        }
        if (/^AMf[-_]|^AEu/i.test(trimmed)) {
            return true;
        }
        return !this.parseJwtPayload(trimmed)?.['companyId'];
    }

    refreshSession(_trigger = 'on-demand'): Promise<string | null> {
        if (this.refreshSessionPromise) {
            return this.refreshSessionPromise;
        }
        this.refreshSessionPromise = this.performSessionRefresh()
            .catch((err) => {
                console.warn('[studies-auth] session refresh error', err);
                return null;
            })
            .finally(() => {
                this.refreshSessionPromise = null;
            });
        return this.refreshSessionPromise;
    }

    startPeriodicSessionRefresh(): void {
        if (typeof window === 'undefined' || this.sessionRefreshTimer) {
            return;
        }
        if (!this.refreshToken?.trim()) {
            return;
        }
        this.sessionRefreshTimer = setInterval(() => {
            if (!this.refreshToken?.trim()) {
                this.stopPeriodicSessionRefresh();
                return;
            }
            void this.refreshSessionIfNeeded();
        }, this.sessionRefreshIntervalMs);
    }

    stopPeriodicSessionRefresh(): void {
        if (this.sessionRefreshTimer) {
            clearInterval(this.sessionRefreshTimer);
            this.sessionRefreshTimer = null;
        }
    }

    private async refreshSessionIfNeeded(): Promise<void> {
        if (!this.refreshToken?.trim() || !this.isTokenExpiringSoon) {
            return;
        }
        await this.refreshSession('periodic');
    }

    private async performSessionRefresh(): Promise<string | null> {
        const authData = this._localStorageService.authorizationData;
        const storedRefresh = authData?.refreshToken?.trim();

        if (!storedRefresh) {
            return authData?.token && !this.isTokenExpired ? authData.token : null;
        }

        if (!this._localStorageService.machineId) {
            this._localStorageService.machineId = crypto.randomUUID();
        }

        if (this.isIdentityRefreshToken(storedRefresh)) {
            const response = await this.refreshIdentitySessionViaFetch(storedRefresh);
            const idToken = (response?.token || '').trim();
            if (idToken) {
                this.persistAuthDataFromSessionRefresh(response, storedRefresh);
                return idToken;
            }
        }

        if (this.isLegacyRefreshToken(storedRefresh) || this.isLegacyAccessToken(authData?.token)) {
            try {
                const legacy = await firstValueFrom(
                    this.getAccessTokenFromRefreshToken(storedRefresh)
                );
                if (legacy?.accessToken) {
                    this.persistAuthData(legacy);
                    return legacy.accessToken;
                }
            } catch (err) {
                console.warn('[studies-auth] legacy token refresh failed', err);
            }
        }

        return authData?.token && !this.isTokenExpired ? authData.token : null;
    }

    private isLegacyAccessToken(token: string | null | undefined): boolean {
        const payload = this.parseJwtPayload(String(token || '').trim());
        if (!payload) {
            return false;
        }
        return !!payload['companyId'] && payload['type'] !== 'refresh_token';
    }

    isIdentityAccessToken(token?: string | null): boolean {
        const raw = String(token ?? this.authToken ?? '').trim();
        if (!raw || this.isLegacyAccessToken(raw)) {
            return false;
        }
        const payload = this.parseJwtPayload(raw);
        if (!payload) {
            return false;
        }
        const iss = String(payload['iss'] ?? '');
        if (iss.includes('securetoken.google.com')) {
            return true;
        }
        if (payload['oid']) {
            return true;
        }
        return !payload['companyId'];
    }

    resolveActiveOrganizationId(): string | null {
        return getLaunchRequestedCompanyId();
    }

    private persistAuthDataFromSessionRefresh(
        response: SessionRefreshResponse,
        fallbackRefresh: string
    ): void {
        const accessToken = (response.token || '').trim();
        if (!accessToken) {
            return;
        }

        let expiresAt: Date;
        if (response.expiresAt) {
            const parsed = Date.parse(response.expiresAt);
            expiresAt = Number.isFinite(parsed)
                ? new Date(parsed)
                : new Date(Date.now() + 60 * 60 * 1000);
        } else if (response.expiresIn) {
            expiresAt = new Date(Date.now() + response.expiresIn * 1000);
        } else {
            try {
                const exp = Utils.parseJwt(accessToken).exp;
                expiresAt = exp
                    ? Utils.getTokenExpireDate(exp)
                    : new Date(Date.now() + 60 * 60 * 1000);
            } catch {
                expiresAt = new Date(Date.now() + 60 * 60 * 1000);
            }
        }

        this._localStorageService.authorizationData = {
            token: accessToken,
            refreshToken: (response.refreshToken || fallbackRefresh).trim(),
            expiresAt,
        };
        if (this.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
    }

    private isJwtExpired(token: string): boolean {
        const payload = this.parseJwtPayload(token);
        const exp = Number(payload?.['exp']);
        if (!exp) {
            return false;
        }
        return exp * 1000 <= Date.now();
    }

    private isJwtExpiringSoon(token: string | undefined): boolean {
        if (!token) {
            return true;
        }
        const payload = this.parseJwtPayload(token);
        const exp = Number(payload?.['exp']);
        if (!exp) {
            return false;
        }
        return exp * 1000 - Date.now() <= this.tokenRefreshBufferMs;
    }

    private parseJwtPayload(token: string): Record<string, unknown> | null {
        try {
            return Utils.parseJwt(token) as Record<string, unknown>;
        } catch {
            return null;
        }
    }

    /**
     * Accept auth handoff from wa-account-v2 at /app-redirect.
     * Supports encrypted blob (?q=&d=) and readable Firebase/Entra tokens (?token=&refreshToken=).
     */
    hydrateAuthFromQueryParams(params: {
        token?: string | null;
        refreshToken?: string | null;
        q?: string | null;
        d?: string | null;
    }): boolean {
        const rawToken = String(params.token || '').trim();
        if (rawToken) {
            if (this.isLegacyAccessToken(rawToken)) {
                return false;
            }
            this.persistAuthData({
                accessToken: rawToken,
                refreshToken: String(params.refreshToken || '').trim(),
            });
            this.applyMachineIdFromQueryParam(params.d);
            if (this.refreshToken?.trim()) {
                this.startPeriodicSessionRefresh();
            }
            return this.hasReadableAuthSession();
        }

        const encrypted = this.decodeQueryParam(params.q);
        if (!encrypted) {
            return false;
        }

        this._localStorageService.authorizationDataEncrypted = encrypted;
        this.applyMachineIdFromQueryParam(params.d);
        const ok = this.hasReadableAuthSession();
        if (ok && this.isLegacyAccessToken(this.authToken)) {
            this._localStorageService.authorizationData = null;
            return false;
        }
        if (ok && !this.refreshToken?.trim()) {
            console.warn(
                '[studies-auth] Session handoff has access token but no refreshToken — periodic refresh and 401 recovery will not run until you sign in again.'
            );
        } else if (ok && this.refreshToken?.trim()) {
            this.startPeriodicSessionRefresh();
        }
        return ok;
    }

    /** After wa-account handoff, exchange Firebase/Entra refresh for a usable API session. */
    async ensureSessionReadyAfterHandoff(): Promise<boolean> {
        const authData = this._localStorageService.authorizationData;
        if (!authData?.token?.trim()) {
            return false;
        }

        if (this.isLegacyAccessToken(authData.token)) {
            return false;
        }

        const refresh = authData.refreshToken?.trim();
        if (
            refresh &&
            (this.isIdentityRefreshToken(refresh) ||
                this.isFirebaseIdentityToken(authData.token))
        ) {
            const token = await this.refreshSession('handoff');
            if (token) {
                return true;
            }
        }

        return this.hasReadableAuthSession();
    }

    private async refreshIdentitySessionViaFetch(
        refreshToken: string
    ): Promise<SessionRefreshResponse | null> {
        const base = this.adminApiBaseUrl.replace(/\/+$/, '');
        try {
            const response = await fetch(`${base}/api/v1/auth/identity/session/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });
            if (!response.ok) {
                return null;
            }
            return (await response.json()) as SessionRefreshResponse;
        } catch (err) {
            console.warn('[studies-auth] identity session refresh failed', err);
            return null;
        }
    }

    private isFirebaseIdentityToken(token: string | null | undefined): boolean {
        const iss = String(this.parseJwtPayload(String(token || '').trim())?.['iss'] || '');
        return iss.includes('securetoken.google.com');
    }

    private decodeQueryParam(value: string | null | undefined): string {
        const raw = String(value || '').trim();
        if (!raw) {
            return '';
        }
        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }

    private applyMachineIdFromQueryParam(machineIdParam: string | null | undefined): void {
        const decoded = this.decodeQueryParam(machineIdParam);
        if (decoded) {
            this._localStorageService.machineId = decoded;
            return;
        }
        if (!this._localStorageService.machineId) {
            this._localStorageService.machineId = crypto.randomUUID();
        }
    }

    private hasReadableAuthSession(): boolean {
        try {
            const authData = this._localStorageService.authorizationData;
            return !!authData?.token?.trim();
        } catch {
            this._localStorageService.authorizationData = null;
            return false;
        }
    }

    appendEncryptedAuthInfo(
        url: string,
        patientId: string = null,
        handoff?: { userId?: string | null; companyId?: string | null }
    ): string {
        const queryParams = [
            `q=${encodeURIComponent(this._localStorageService.authorizationDataEncrypted)}`
        ];
        if (patientId) {
            queryParams.push(`id=${encodeURIComponent(patientId)}`);
        }
        if (this._localStorageService.machineId) {
            queryParams.push(`d=${encodeURIComponent(this._localStorageService.machineId)}`);
        }
        const org = this.resolveCrossAppHandoff(handoff);
        if (org.companyId) {
            queryParams.push(`companyId=${encodeURIComponent(org.companyId)}`);
        }
        if (org.userId) {
            queryParams.push(`userId=${encodeURIComponent(org.userId)}`);
        }
        return `${url}/app-redirect?${queryParams.join('&')}`;
    }

    private resolveCrossAppHandoff(handoff?: {
        userId?: string | null;
        companyId?: string | null;
    }): { userId?: string; companyId?: string } {
        const userId = handoff?.userId?.trim() || undefined;
        let companyId =
            handoff?.companyId?.trim() ||
            this.resolveActiveOrganizationId() ||
            undefined;
        if (!companyId) {
            const jwtCompanyId = this.parseJwtPayload(this.authToken)?.['companyId'];
            if (typeof jwtCompanyId === 'string' && jwtCompanyId.trim()) {
                companyId = jwtCompanyId.trim();
            }
        }
        return { userId, companyId };
    }
}

export interface AuthData {
    token: string;
    refreshToken: string;
    expiresAt: Date;
}

export class User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    designation: string;
    title: string;
    status: UserStatus;
    type: UserType;
    globalAdmin: boolean;
    isOwner: boolean;
    npi: string;
    companyName: string;
    companyId: string;
    constructor(userType: UserType = UserType.BillingPhysician, title: string = '') {
        this.type = userType;
        this.title = title;
    }
}

export interface UserCompany {
    userId: string;
    isPinned: boolean;
    isPrimary?: boolean;
    isHidden: boolean;
    companyId: string;
    companyName: string;
    company?: {
        id: string;
        name: string;
    };
}

export enum UserStatus {
    Active = 1,
    Pending,
    Suspended
}

export enum UserType {
    BillingPhysician = 1,
    Nurse,
    MedicalAssistent,
    NonMedicalPersonal
}
